/* SocialPulse SSO — hand-rolled OIDC Authorization Code + PKCE (S256).
   Works as a public client today; when the SpLink registry credentials land, the
   backend adds client_secret_post server-side and the redirect path flips to
   /auth/callback via REACT_APP_SOCIALPULSE_REDIRECT_PATH — no code change. */
const ISSUER = process.env.REACT_APP_SOCIALPULSE_OIDC_ISSUER;
const CLIENT_ID = process.env.REACT_APP_SOCIALPULSE_CLIENT_ID || process.env.REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID;

export const SOCIALPULSE_SSO_READY = Boolean(ISSUER && CLIENT_ID);

const redirectUri = () =>
  `${window.location.origin}${process.env.REACT_APP_SOCIALPULSE_REDIRECT_PATH || "/auth/socialpulse-callback"}`;

const b64url = (bytes) =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const randomString = (n = 32) => {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return b64url(out);
};

async function s256(verifier) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

async function discover() {
  // Same-origin call — our backend does the real OIDC discovery (avoids CORS).
  const base = process.env.REACT_APP_BACKEND_URL;
  const r = await fetch(`${base}/api/auth/oidc-config`);
  if (!r.ok) throw new Error("SocialPulse SSO is not available right now");
  return r.json();
}

/* Redirect the browser to SocialPulse's authorize screen. */
export async function startSocialPulseLogin(returnTo = "/") {
  const { authorization_endpoint, client_id, redirect_path } = await discover();
  const ru = redirect_path ? `${window.location.origin}${redirect_path}` : redirectUri();
  const state = randomString();
  const nonce = randomString();
  const verifier = randomString(32);
  sessionStorage.setItem("sp_oidc_tx", JSON.stringify({ state, nonce, verifier, returnTo, redirect_uri: ru }));
  const params = new URLSearchParams({
    client_id: client_id || CLIENT_ID,
    response_type: "code",
    redirect_uri: ru,
    scope: "openid profile email",
    state,
    nonce,
    code_challenge: await s256(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`${authorization_endpoint}?${params}`);
}

/* Step-up re-verification in a POPUP: opens SocialPulse authorize, the callback
   page (popup mode) exchanges the code server-side and posts a fresh Firebase
   custom token back — the opener re-signs-in, refreshing auth_time without
   losing the pending sensitive action. */
export async function startSocialPulseStepUp() {
  const { authorization_endpoint, client_id, redirect_path } = await discover();
  const ru = redirect_path ? `${window.location.origin}${redirect_path}` : redirectUri();
  const state = `popup-${randomString()}`;
  const nonce = randomString();
  const verifier = randomString(32);
  // localStorage (not sessionStorage): the popup is a separate tab context
  localStorage.setItem("sp_oidc_popup_tx", JSON.stringify({ state, nonce, verifier, redirect_uri: ru }));
  const params = new URLSearchParams({
    client_id: client_id || CLIENT_ID,
    response_type: "code",
    redirect_uri: ru,
    scope: "openid profile email",
    state,
    nonce,
    code_challenge: await s256(verifier),
    code_challenge_method: "S256",
    prompt: "login",
  });
  const w = window.open(`${authorization_endpoint}?${params}`, "sp-stepup", "width=480,height=680,popup=yes");
  if (!w) throw new Error("Popup blocked — please allow popups for this site and try again");
  return new Promise((resolve, reject) => {
    const cleanup = () => { clearTimeout(timer); clearInterval(poll); window.removeEventListener("message", onMsg); };
    const timer = setTimeout(() => { cleanup(); reject(new Error("Re-verification timed out")); }, 180000);
    const poll = setInterval(() => { if (w.closed) { cleanup(); reject(new Error("Re-verification was cancelled")); } }, 800);
    const onMsg = (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== "sp-stepup") return;
      cleanup();
      if (e.data.error) reject(new Error(e.data.error));
      else resolve(e.data.custom_token);
    };
    window.addEventListener("message", onMsg);
  });
}

/* Popup-mode completion on the callback page. Returns null when not a popup flow. */
export function completeSocialPulsePopup() {
  const query = new URLSearchParams(window.location.search);
  const state = query.get("state") || "";
  if (!state.startsWith("popup-") || !window.opener) return null;
  const tx = JSON.parse(localStorage.getItem("sp_oidc_popup_tx") || "null");
  localStorage.removeItem("sp_oidc_popup_tx");
  if (query.get("error")) return { error: query.get("error_description") || query.get("error") };
  const code = query.get("code");
  if (!code || !tx || state !== tx.state) return { error: "Re-verification session expired" };
  return { code, code_verifier: tx.verifier, redirect_uri: tx.redirect_uri || redirectUri(), nonce: tx.nonce };
}

/* On the callback page: validate state, hand {code, verifier} to the caller —
   the token exchange happens server-side in /api/auth/oidc-session (no CORS). */
export async function completeSocialPulseLogin() {
  const query = new URLSearchParams(window.location.search);
  if (query.get("error")) throw new Error(query.get("error_description") || query.get("error"));
  const code = query.get("code");
  const tx = JSON.parse(sessionStorage.getItem("sp_oidc_tx") || "null");
  sessionStorage.removeItem("sp_oidc_tx");
  if (!code || !tx || query.get("state") !== tx.state) throw new Error("Sign-in session expired — please try again");
  return { code, code_verifier: tx.verifier, redirect_uri: tx.redirect_uri || redirectUri(), nonce: tx.nonce, returnTo: tx.returnTo || "/" };
}
